import type { ReactNode } from 'react';
import { appSurface, type BadgeTone } from '../lib/web-ui-style';

export function PageHeader({
  eyebrow,
  title,
  description,
  action,
  badge,
}: {
  eyebrow?: string;
  title: string;
  description?: ReactNode;
  action?: ReactNode;
  badge?: ReactNode;
}) {
  return (
    <header className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
      <div>
        {eyebrow && <div className={`${appSurface.eyebrow} text-slate-500`}>{eyebrow}</div>}
        <h1 className="mt-2 text-3xl font-bold tracking-tight text-slate-950">{title}</h1>
        {description && <p className="mt-2 text-sm text-slate-600">{description}</p>}
      </div>
      {action ?? badge ?? null}
    </header>
  );
}

export function SectionCard({
  children,
  className = '',
  hover = false,
}: {
  children: ReactNode;
  className?: string;
  hover?: boolean;
}) {
  return (
    <div className={`${appSurface.panel} ${hover ? appSurface.panelHover : ''} ${className}`.trim()}>
      {children}
    </div>
  );
}

export function MetricCard({
  icon,
  label,
  value,
  loading = false,
  tone = 'blue',
}: {
  icon?: ReactNode;
  label: string;
  value: ReactNode;
  loading?: boolean;
  tone?: BadgeTone;
}) {
  return (
    <div className={`${appSurface.metric} flex items-center gap-4`}>
      {icon && <div className={`${appSurface.iconBox} ${metricIconTone[tone]}`}>{icon}</div>}
      <div>
        <div className="text-xs font-semibold uppercase tracking-[0.16em] text-slate-500">
          {label}
        </div>
        <div className="mt-1 text-3xl font-bold text-slate-950 tabular-nums">
          {loading ? <span className="text-slate-300">—</span> : value}
        </div>
      </div>
    </div>
  );
}

export function StatusBadge({
  children,
  tone = 'slate',
  size = 'md',
  className = '',
}: {
  children: ReactNode;
  tone?: BadgeTone;
  size?: 'sm' | 'md';
  className?: string;
}) {
  const sizeClass = size === 'sm'
    ? 'px-1.5 py-0.5 text-[10px] uppercase tracking-wide'
    : 'px-2 py-0.5 text-xs';
  return (
    <span className={`inline-flex items-center rounded-full border font-medium ${sizeClass} ${badgeTone[tone]} ${className}`.trim()}>
      {children}
    </span>
  );
}

const metricIconTone: Record<BadgeTone, string> = {
  amber: 'bg-amber-50',
  blue: 'bg-blue-50',
  emerald: 'bg-emerald-50',
  purple: 'bg-purple-50',
  rose: 'bg-rose-50',
  slate: 'bg-slate-50',
};

const badgeTone: Record<BadgeTone, string> = {
  amber: 'border-amber-200 bg-amber-50 text-amber-700',
  blue: 'border-blue-200 bg-blue-50 text-blue-700',
  emerald: 'border-emerald-200 bg-emerald-50 text-emerald-700',
  purple: 'border-purple-200 bg-purple-50 text-purple-700',
  rose: 'border-rose-200 bg-rose-50 text-rose-700',
  slate: 'border-slate-200 bg-slate-50 text-slate-600',
};
